import React, { useMemo } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ConfluenceIcon } from '@atlaskit/logo';
import { AppLogo, CustomLogo } from '@atlaskit/navigation-system/top-nav-items';

import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';
import { WIKI } from '@confluence/named-routes/entry-points/routes.ts';

import type { TopNavigationQuery$data } from './__generated__/TopNavigationQuery.graphql.ts';

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ './analyticsCallbacks.ts'))
			.fireLogoClickedAnalytics,
);

type SiteLogoButtonProps = {
	siteConfiguration: TopNavigationQuery$data['siteConfiguration'] | undefined;
	siteSettings: TopNavigationQuery$data['siteSettings'] | undefined;
	siteDescription: TopNavigationQuery$data['siteDescription'] | undefined;
	onLoadCustomLogo?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
};

export const SiteLogoButton = ({
	siteConfiguration,
	siteSettings,
	siteDescription,
	onLoadCustomLogo,
}: SiteLogoButtonProps): React.JSX.Element => {
	const routeDataRef = useRouteDataRef();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onClick = useLazyClickAnalytics(createAnalyticsEvent, routeDataRef.current?.routeName);
	const siteTitle = siteSettings && siteSettings.siteTitle;
	const CustomNavLogo = useMemo(() => {
		if (siteConfiguration?.customSiteLogo && siteDescription?.logoUrl) {
			return () => (
				<img alt="" src={siteDescription.logoUrl ?? undefined} onLoad={onLoadCustomLogo} />
			);
		}

		return null;
	}, [onLoadCustomLogo, siteConfiguration?.customSiteLogo, siteDescription?.logoUrl]);

	return CustomNavLogo ? (
		<CustomLogo
			label={siteTitle ?? 'Confluence'}
			logo={CustomNavLogo}
			icon={CustomNavLogo}
			onClick={onClick}
			href={WIKI.toUrl()}
		/>
	) : (
		<AppLogo
			label={siteTitle ?? 'Confluence'}
			icon={ConfluenceIcon}
			name="Confluence"
			onClick={onClick}
			href={WIKI.toUrl()}
		/>
	);
};
